import { Header } from 'components/Header';
import { HeaderTitle } from 'components/Header/HeaderTitle';
import { PageTitle } from 'components/PageTitle';
import { PAGES } from 'constants/pages';
import {
    useUpdateDeliveryPlanningBatchMutation,
    useGetAllSitesDeliveryPlanningForThreeWeeksQuery,
    useGetAllRegularTotemClosedDayQuery,
    useDeliveryPlanningUpdatedSubscription,
} from 'data/__generated__';
import { formatDateAsAnniversary, getNextMondayDate, transformFrenchToBirthDayDate } from 'helpers/dateTimes';
import { useCallback, useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm, FormProvider } from 'react-hook-form';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import { CalendarOff } from 'lucide-react';
import { Loader } from 'components/Loader';
import { ActionBar } from 'components/ActionBar';
import { DeliveryPlanningTable } from './components/DeliveryPlanningTable';
import { DeliveryPlanningFilters } from './components/DeliveryPlanningFilters';
import { BatchClosurePopup } from './components/BatchClosurePopup';
import { DeliveryPlanningFilterBar } from './components/DeliveryPlanningFilterBar';
import {
    getThreeWeeksDates,
    getThreeWeeksLabels,
    filterThreeWeeksBySelectedDays,
    getMondayOfWeek,
    isDateClosed,
} from './utils/dateUtils';
import { useDeliveryPlanningFiltersUrlSync } from './utils/useDeliveryPlanningFiltersUrlSync';

interface ParamTypes extends Record<string, string | undefined> {
    selectedDate?: string;
}

// Structure des données du formulaire
type DeliveryPlanningFormData = {
    sites: Record<string, Record<string, boolean>>; // siteId -> date -> isActive
};

export const DeliveryPlanning = () => {
    const navigate = useNavigate();
    const { selectedDate: selectedDateFromUrl } = useParams<ParamTypes>();
    const [selectedDays, setSelectedDays] = useState<boolean[]>([true, true, true, true, true, false, false]);
    const [sortConfig, setSortConfig] = useState<{ date: string; direction: 'asc' | 'desc' } | null>(null);
    const [selectedSiteIds, setSelectedSiteIds] = useState<Set<string>>(new Set());
    const [isBatchClosureOpen, setIsBatchClosureOpen] = useState(false);
    const [filters, setFilters] = useDeliveryPlanningFiltersUrlSync();

    const initialDate = selectedDateFromUrl ? new Date(selectedDateFromUrl) : getNextMondayDate();
    const selectedDate = getMondayOfWeek(initialDate);

    // Calcul des dates et labels des 3 semaines
    const threeWeeksData = getThreeWeeksDates(selectedDate);
    const threeWeeksLabels = getThreeWeeksLabels(selectedDate);

    const { visibleDates, visibleLabels, weekDelimiters } = filterThreeWeeksBySelectedDays(
        threeWeeksData,
        threeWeeksLabels,
        selectedDays,
    );

    const mondayDateString = formatDateAsAnniversary({ dateTime: selectedDate, useNewFormat: true });

    // Récupérer les données pour les 3 semaines
    const {
        loading: deliveryPlanningLoading,
        data: deliveryPlanningData,
        error: deliveryPlanningError,
        refetch: refetchDeliveryPlanning,
    } = useGetAllSitesDeliveryPlanningForThreeWeeksQuery({
        variables: { mondayDate: mondayDateString },
        fetchPolicy: 'cache-and-network',
    });

    const {
        loading: totemClosedDaysLoading,
        data: totemClosedDaysData,
        error: totemClosedDaysError,
    } = useGetAllRegularTotemClosedDayQuery({
        fetchPolicy: 'cache-and-network',
    });

    useDeliveryPlanningUpdatedSubscription({
        onData: () => {
            refetchDeliveryPlanning();
        },
    });

    const sitePlannings = deliveryPlanningData?.getAllSitesDeliveryPlanningForThreeWeeks || [];
    const totemClosedDays = totemClosedDaysData?.getAllRegularTotemClosedDay || [];

    const filteredSitePlannings = useMemo(() => {
        const nameFilter = filters.name.trim().toLowerCase();
        return sitePlannings.filter((sitePlanning) => {
            const { site } = sitePlanning;
            if (nameFilter && !site.name.toLowerCase().includes(nameFilter)) return false;
            if (filters.deliveryTypes.length > 0 && !filters.deliveryTypes.includes(site.deliveryType)) return false;
            if (filters.isSecured !== undefined && site.isSecured !== filters.isSecured) return false;
            if (filters.hasSensei !== undefined && site.hasSensei !== filters.hasSensei) return false;

            if (filters.launchDateRange?.gte || filters.launchDateRange?.lte) {
                if (!site.launchDate) return false;
                const launchTs = new Date(transformFrenchToBirthDayDate(site.launchDate)).getTime();
                if (filters.launchDateRange.gte && launchTs < filters.launchDateRange.gte) return false;
                if (filters.launchDateRange.lte && launchTs > filters.launchDateRange.lte) return false;
            }

            if (filters.removalDateRange?.gte || filters.removalDateRange?.lte) {
                if (!site.removalDate) return false;
                const removalTs = new Date(site.removalDate).getTime();
                if (filters.removalDateRange.gte && removalTs < filters.removalDateRange.gte) return false;
                if (filters.removalDateRange.lte && removalTs > filters.removalDateRange.lte) return false;
            }

            return true;
        });
    }, [sitePlannings, filters]);

    // Mutations
    const [updateDeliveryPlanningBatch, { loading: saveLoading }] = useUpdateDeliveryPlanningBatchMutation();

    // Calculer les valeurs par défaut pour le formulaire à partir des vraies données
    const defaultValues = useMemo(() => {
        const initialFormData: Record<string, Record<string, boolean>> = {};

        sitePlannings.forEach((sitePlanning) => {
            const { site, deliveryPlanning } = sitePlanning;
            initialFormData[site._id] = {};

            // Initialiser toutes les dates à false
            threeWeeksData.allDates.forEach((date) => {
                initialFormData[site._id][date] = false;
            });

            // Marquer les dates actives comme vraies selon la valeur isActive
            deliveryPlanning.forEach((deliveryDate: { date: string; isManual: boolean; isActive: boolean }) => {
                if (threeWeeksData.allDates.includes(deliveryDate.date)) {
                    initialFormData[site._id][deliveryDate.date] = deliveryDate.isActive;
                }
            });
        });

        return { sites: initialFormData };
    }, [sitePlannings, threeWeeksData.allDates]);

    const methods = useForm<DeliveryPlanningFormData>({
        values: defaultValues,
    });

    const {
        handleSubmit,
        formState: { isDirty },
        watch,
        setValue,
    } = methods;

    const setSelectedDate = useCallback(
        (date: Date) => {
            const mondayOfSelectedWeek = getMondayOfWeek(date);
            navigate(
                `/delivery-planning/${formatDateAsAnniversary({
                    dateTime: mondayOfSelectedWeek,
                    useNewFormat: true,
                })}`,
            );
        },
        [navigate],
    );

    // Surveiller les données du formulaire
    const formData = watch();
    const deliveryPlanningState = formData.sites || {};

    const handleDeliveryPlanningUpdate = useCallback(
        (siteId: string, date: string) => {
            const currentValue = deliveryPlanningState[siteId]?.[date] || false;
            setValue(`sites.${siteId}.${date}`, !currentValue, { shouldDirty: true });
        },
        [deliveryPlanningState, setValue],
    );

    const handleSelectAllForDay = useCallback(
        (date: string, isChecked: boolean) => {
            filteredSitePlannings.forEach((sitePlanning) => {
                const { site } = sitePlanning;
                const isClosedDate = isDateClosed(
                    date,
                    site.closingDatesRanges,
                    totemClosedDays,
                    site.launchDate,
                    site.removalDate,
                );

                if (!isClosedDate) {
                    setValue(`sites.${site._id}.${date}`, isChecked, { shouldDirty: true });
                }
            });
        },
        [filteredSitePlannings, totemClosedDays, setValue],
    );

    const handleDaySelect = useCallback(
        (dayIndex: number) => {
            const newSelectedDays = [...selectedDays];
            newSelectedDays[dayIndex] = !newSelectedDays[dayIndex];
            setSelectedDays(newSelectedDays);
        },
        [selectedDays],
    );

    const handleSort = useCallback((date: string) => {
        setSortConfig((prev) => {
            if (prev?.date === date) {
                return prev.direction === 'asc' ? { date, direction: 'desc' } : null;
            }
            return { date, direction: 'asc' };
        });
    }, []);

    const handleSiteSelect = useCallback((siteId: string) => {
        setSelectedSiteIds((prev) => {
            const next = new Set(prev);
            if (next.has(siteId)) {
                next.delete(siteId);
            } else {
                next.add(siteId);
            }
            return next;
        });
    }, []);

    const handleSelectAllSites = useCallback(
        (isChecked: boolean) => {
            setSelectedSiteIds(isChecked ? new Set(filteredSitePlannings.map((s) => s.site._id)) : new Set());
        },
        [filteredSitePlannings],
    );

    const selectedSitesForPopup = useMemo(
        () =>
            sitePlannings
                .filter(({ site }) => selectedSiteIds.has(site._id))
                .map(({ site }) => ({ _id: site._id, name: site.name })),
        [sitePlannings, selectedSiteIds],
    );

    const onSubmit = handleSubmit(async (data) => {
        try {
            const changes: Array<{ siteId: string; date: string; isActive: boolean }> = [];

            // Détecter les changements par rapport aux valeurs initiales du formulaire
            Object.entries(data.sites).forEach(([siteId, siteDates]) => {
                Object.entries(siteDates).forEach(([date, isChecked]) => {
                    const initialValue = defaultValues.sites[siteId]?.[date] || false;
                    // Si la valeur a changé par rapport à l'état initial, on ajoute le changement
                    if (isChecked !== initialValue) {
                        changes.push({ siteId, date, isActive: isChecked });
                    }
                });
            });
            const result = await updateDeliveryPlanningBatch({
                variables: {
                    deliveryPlanningBatchUpdate: changes,
                },
            });

            if (result.data?.updateDeliveryPlanningBatch.success) {
                toast.success(`${changes.length} modification(s) appliquée(s) avec succès`);
                await refetchDeliveryPlanning();
            } else {
                toast.error(`Erreur lors de la sauvegarde`);
            }
        } catch (error) {
            toast.error(`Erreur lors de la sauvegarde: ${error}`);
        }
    });

    const isLoading = deliveryPlanningLoading || totemClosedDaysLoading;

    if (isLoading) {
        return (
            <MainContainer>
                <Loader />
            </MainContainer>
        );
    }

    if (deliveryPlanningError || totemClosedDaysError) {
        throw new Error('Une erreur est survenue lors de la récupération des données');
    }

    return (
        <FormProvider {...methods}>
            <MainContainer>
                <Form onSubmit={onSubmit}>
                    <StyledHeader>
                        <HeaderTitle>
                            <PageTitle page={PAGES.deliveryPlanning} />
                        </HeaderTitle>
                        <DeliveryPlanningFilters
                            selectedDate={selectedDate}
                            selectedDays={selectedDays}
                            hasChanges={isDirty}
                            onDateChange={setSelectedDate}
                            onDaySelect={handleDaySelect}
                            saveLoading={saveLoading}
                        />
                    </StyledHeader>
                    <BodyRow>
                        <ScrollableContainer>
                            <Legend>
                                <LegendItem>
                                    <LegendCheckbox type="checkbox" checked readOnly $color="#2196F3" />
                                    <span>Livraison prévue</span>
                                </LegendItem>
                                <LegendItem>
                                    <LegendCheckbox type="checkbox" readOnly $color="#bacfe7" $uncheckedBackground />
                                    <span>Livraison automatique</span>
                                </LegendItem>
                                <LegendItem>
                                    <LegendCheckbox type="checkbox" checked readOnly $color="#ff9800" />
                                    <span>Livraison date de lancement/fermeture</span>
                                </LegendItem>
                                <LegendItem>
                                    <ClosedSiteIndicator />
                                    <span>Site fermé</span>
                                </LegendItem>
                                <LegendItem>
                                    <LegendCheckbox type="checkbox" readOnly $color="#ffffff" />
                                    <span>Pas de livraison</span>
                                </LegendItem>
                            </Legend>
                            <DeliveryPlanningFilterBar filters={filters} onChange={setFilters} />
                            <DeliveryPlanningTable
                                sitePlannings={filteredSitePlannings}
                                visibleDates={visibleDates}
                                visibleLabels={visibleLabels}
                                weekDelimiters={weekDelimiters}
                                deliveryPlanningState={deliveryPlanningState}
                                totemClosedDays={totemClosedDays}
                                sortConfig={sortConfig}
                                onDeliveryPlanningUpdate={handleDeliveryPlanningUpdate}
                                onSelectAllForDay={handleSelectAllForDay}
                                onSort={handleSort}
                                selectedSiteIds={selectedSiteIds}
                                onSiteSelect={handleSiteSelect}
                                onSelectAllSites={handleSelectAllSites}
                            />
                        </ScrollableContainer>
                        <ActionBar
                            isInitiallyOpen={selectedSiteIds.size > 0}
                            actionSections={[
                                {
                                    title: 'Actions sites',
                                    actions: [
                                        {
                                            icon: <CalendarOff />,
                                            label: `Ajouter une fermeture${
                                                selectedSiteIds.size > 0 ? ` (${selectedSiteIds.size})` : ''
                                            }`,
                                            onClick: () => setIsBatchClosureOpen(true),
                                            hidden: selectedSiteIds.size === 0,
                                            dataTest: 'open-batch-closure',
                                        },
                                    ],
                                },
                            ]}
                        />
                    </BodyRow>
                </Form>
                <BatchClosurePopup
                    isOpen={isBatchClosureOpen}
                    setIsOpen={setIsBatchClosureOpen}
                    selectedSites={selectedSitesForPopup}
                    onApplied={async () => {
                        setSelectedSiteIds(new Set());
                        await refetchDeliveryPlanning();
                    }}
                />
            </MainContainer>
        </FormProvider>
    );
};

const MainContainer = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    height: 100%;
    background-color: ${({ theme }) => theme.backgroundColor};
    color: ${({ theme }) => theme.textColor};
    overflow: hidden;
`;

const Form = styled.form`
    display: flex;
    flex-direction: column;
    flex: 1;
    height: 100%;
`;

const StyledHeader = styled(Header)`
    z-index: 1000;
`;

const BodyRow = styled.div`
    display: flex;
    flex: 1;
    overflow: hidden;
`;

const ScrollableContainer = styled.div`
    margin: 20px;
    overflow: auto;
    flex: 1;
    display: flex;
    flex-direction: column;
    min-width: 0;
    max-width: 100%;

    &::-webkit-scrollbar {
        height: 10px;
        width: 10px;
    }

    &::-webkit-scrollbar-track {
        background: ${({ theme }) => theme.backgroundColor};
    }

    &::-webkit-scrollbar-thumb {
        background: ${({ theme }) => theme.lightBorderColor};
        border-radius: 5px;
    }
`;

const Legend = styled.div`
    display: flex;
    gap: 20px;
    margin-bottom: 15px;
    padding: 10px 15px;
    background-color: ${({ theme }) => theme.cardBackgroundColor};
    border-radius: 8px;
    border: 1px solid ${({ theme }) => theme.lightBorderColor};
`;

const LegendItem = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
`;

const LegendCheckbox = styled.input<{ $color: string; $uncheckedBackground?: boolean }>`
    width: 20px;
    height: 20px;
    accent-color: ${({ $color }) => $color};
    pointer-events: none;

    ${({ $uncheckedBackground, $color }) =>
        $uncheckedBackground &&
        `
        background-color: ${$color};
        border: 2px solid ${$color};
        appearance: none;
        border-radius: 3px;
    `}
`;

const ClosedSiteIndicator = styled.div`
    width: 20px;
    height: 20px;
    position: relative;
    border: 2px solid ${({ theme }) => theme.lightBorderColor};
    border-radius: 3px;
    background: repeating-linear-gradient(
        45deg,
        transparent,
        transparent 4px,
        ${({ theme }) => theme.lightBorderColor} 4px,
        ${({ theme }) => theme.lightBorderColor} 6px
    );
`;
